import React from "react";
import { StyledBlogMarkdownContent, blogMarkdownContentClasses } from "./StyledBlogMarkdownContent";
import { IBlogMarkdownImage } from "../../IBlog";

interface BlogMarkdownContentProps {
  value: string;
  images?: Record<string, IBlogMarkdownImage>;
}

export const BlogMarkdownContent = ({ value, images = {} }: BlogMarkdownContentProps) => {
  // Helper function to extract image source from imported image objects
  const getImageSrc = (imageValue: any): string => {
    if (typeof imageValue === 'string') {
      return imageValue;
    }
    if (typeof imageValue === 'object' && imageValue !== null) {
      // Handle webpack imported images - they can be strings or objects with default/src
      if (imageValue.default) {
        return typeof imageValue.default === 'string' ? imageValue.default : getImageSrc(imageValue.default);
      }
      if (imageValue.src) {
        return imageValue.src;
      }
      // If it's an object, try to stringify it (might be a module)
      return String(imageValue);
    }
    return String(imageValue);
  };

  // Basic markdown rendering - can be enhanced with a markdown library later
  const renderMarkdown = (markdown: string) => {
    // Trim the markdown
    let html = markdown.trim();
    
    // Split into lines for processing
    const lines = html.split('\n');
    const processedLines: string[] = [];
    let inList = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // A <br> on its own line clears floated images.
      if (/^<br(\s+[^>]*)?\s*\/?>$/i.test(line)) {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        processedLines.push('<div class="blog-clear"></div>');
        continue;
      }

      // Handle images ![image: key] - process before other line types
      const imageMatch = line.match(/^!\[image:\s*([^\]]+)\]$/);
      if (imageMatch) {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        const [, imageKey] = imageMatch;
        const imageKeyTrimmed = imageKey.trim();
        const imageData = images?.[imageKeyTrimmed];
        
        if (imageData) {
          // Extract the actual image source
          const imageSrc = getImageSrc(imageData.value);
          const alt = imageData.alt || "";
          const position = imageData.position;
          const shouldFloat = Boolean(imageData.float) && position !== "center";
          
          // Handle width: use provided width, or default to 200px for floating images, auto for non-floating
          const defaultWidth = shouldFloat ? "200px" : "auto";
          const widthValue = imageData.width !== undefined 
            ? (typeof imageData.width === 'number' ? `${imageData.width}px` : imageData.width)
            : defaultWidth;
          
          // Build image HTML with width support
          const imageWidthStyle = widthValue !== "auto" ? `width: ${widthValue}; ` : "";
          let imgHtml = `<img src="${imageSrc}" alt="${alt}" style="${imageWidthStyle}max-width: 100%; height: auto; border-radius: 8px; display: block;" />`;

          const alignImage = position === "left" || position === "right" || position === "center";
          if (alignImage) {
            let containerStyle = `margin-bottom: 16px; width: ${widthValue}; max-width: 100%;`;
            if (shouldFloat && position === "right") {
              containerStyle += " float: right; margin-left: 20px;";
            } else if (shouldFloat && position === "left") {
              containerStyle += " float: left; margin-right: 20px;";
            } else if (position === "center") {
              containerStyle += " margin-left: auto; margin-right: auto;";
            } else if (position === "right") {
              containerStyle += " margin-left: auto; margin-right: 0;";
            } else {
              containerStyle += " margin-right: auto; margin-left: 0;";
            }

            let containerHtml = `<div class="blog-image-container" style="${containerStyle}">`;
            containerHtml += imgHtml;
            if (imageData.copyright) {
              containerHtml += `<p style="font-size: 12px; color: #666; margin-top: 4px; margin-bottom: 0; font-style: italic;">${imageData.copyright}</p>`;
            }
            containerHtml += `</div>`;
            processedLines.push(containerHtml);
          } else {
            processedLines.push(imgHtml);
            if (imageData.copyright) {
              processedLines.push(`<p style="font-size: 12px; color: #666; margin-top: 4px; font-style: italic;">${imageData.copyright}</p>`);
            }
          }
        } else {
          // Image key not found, render as plain text or placeholder
          processedLines.push(`<p>[Image not found: ${imageKeyTrimmed}]</p>`);
        }
        continue;
      }
      
      // Handle blockquotes
      if (line.startsWith('> ')) {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        processedLines.push(`<blockquote>${line.substring(2)}</blockquote>`);
        continue;
      }
      
      // Handle headings (h3, h4, h5)
      if (line.startsWith('# ')) {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        processedLines.push(`<h3>${line.substring(2)}</h3>`);
        continue;
      }
      if (line.startsWith('## ')) {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        processedLines.push(`<h4>${line.substring(3)}</h4>`);
        continue;
      }
      if (line.startsWith('### ')) {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        processedLines.push(`<h5>${line.substring(4)}</h5>`);
        continue;
      }
      
      // Handle list items
      if (line.startsWith('- ')) {
        if (!inList) {
          processedLines.push('<ul>');
          inList = true;
        }
        processedLines.push(`<li>${line.substring(2)}</li>`);
        continue;
      }
      
      // End list if we were in one
      if (inList && line !== '') {
        processedLines.push('</ul>');
        inList = false;
      }
      
      // Regular paragraph
      if (line !== '') {
        processedLines.push(`<p>${line}</p>`);
      }
    }
    
    // Close list if still open
    if (inList) {
      processedLines.push('</ul>');
    }
    
    html = processedLines.join('\n');

    // Keep generated tags (images, headings, lists) out of the inline pass.
    // Underscores in image filenames would otherwise be treated as italics.
    const protectedTags: string[] = [];
    html = html.replace(/<[^>]+>/g, (tag) => {
      protectedTags.push(tag);
      return `\u0000${protectedTags.length - 1}\u0000`;
    });
    
    // Handle bold **text** (must come before italic to avoid conflicts)
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Handle italic *text* or _text_ (after bold to avoid conflicts)
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.*?)_/g, '<em>$1</em>');
    
    // Handle links [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

    html = html.replace(/\u0000(\d+)\u0000/g, (_, index) => protectedTags[Number(index)]);
    
    return html;
  };

  return (
    <StyledBlogMarkdownContent 
      className={blogMarkdownContentClasses.root}
      dangerouslySetInnerHTML={{ __html: renderMarkdown(value) }}
    />
  );
};

